import type { GraphQLQuery, GraphQLResult } from "@aws-amplify/api";

import {
  Expand,
  ExpandRecursively,
  Query,
  QueryArgs,
  Scalars,
  Mutation,
  MutationArgs,
  ScalarKeys,
  NonScalarKeys,
} from "@examspark/types";

import { GraphQLAPI } from ".";

// Cons, as in the lisp function. Take an array of strings and return
// that array without the first element.
type Cons<V> = V extends []
  ? never
  : V extends [string]
    ? []
    : V extends [string, ...infer R]
      ? R extends string[]
        ? R
        : never
      : never;

type TrimLeft<V extends string> = V extends
  | ` ${infer R}`
  | `
${infer R}`
  ? TrimLeft<R>
  : V;
type TrimRight<V extends string> = V extends
  | `${infer R} `
  | `${infer R}
`
  ? TrimRight<R>
  : V;

// Trim a string of leading and trailing whitespace. Super useful in our
type Trim<V extends string> = TrimLeft<TrimRight<V>>;

/// This is the old, recurision-limited split function. Way easier to read.
// type Split<
//   S extends string,
//   D extends string
// > = Trim<S> extends `${infer T}${D}${infer U}`
//   ? [Trim<T>, ...Split<Trim<U>, D>]
//   : [Trim<S>];

type _Split<S extends string, D extends string, A extends string[] = []> =
  Trim<S> extends `${infer T}${D}${infer U}`
    ? {
        __rec: _Split<Trim<U>, D, [...A, Trim<T>]>;
      }
    : { __rec: [...A, Trim<S>] };

type _Recurse<T> = T extends { __rec: never }
  ? never
  : T extends { __rec: { __rec: infer U } }
    ? { __rec: _Recurse<U> }
    : T extends { __rec: infer U }
      ? U
      : T;

type Recurse<T> = T extends { __rec: unknown } ? Recurse<_Recurse<T>> : T;

export type Split<S extends string, D extends string> = Recurse<_Split<S, D>>;

// Slurp up all the characters until we hit a closing brace matching the one we started on
export type TakeUntilMatchedBrace<
  V extends string[],
  Delimeter extends [string, string],
  pairs extends string[] = [],
> = V extends [string, ...infer R]
  ? V[0] extends Delimeter[1]
    ? pairs extends [""]
      ? R
      : TakeUntilMatchedBrace<Cons<V>, Delimeter, Cons<pairs>>
    : V[0] extends Delimeter[0]
      ? TakeUntilMatchedBrace<Cons<V>, Delimeter, [...pairs, ""]>
      : TakeUntilMatchedBrace<Cons<V>, Delimeter, pairs>
  : [];

// prettier-ignore
export type ParsedGraphQLResult<
  T extends string[],
  Foo,
  /* eslint-disable-next-line @typescript-eslint/ban-types */
  SyntesizedType = {}
> = T extends []
  ? SyntesizedType
  : T[0] extends ScalarKeys<Foo>
    ? ParsedGraphQLResult<Cons<T>, Foo, SyntesizedType & Pick<Foo, T[0]>>
    : T[0] extends NonScalarKeys<Foo>
      ? T[1] extends "{"
        ? ParsedGraphQLResult<
          TakeUntilMatchedBrace<T, ["{", "}"]>,
          Foo,
          SyntesizedType & {
            [nestedObjectKey in T[0]]: NonNullable<Foo[T[0]]> extends Array<
              infer P
            >
              ? Array<
                  ParsedGraphQLResult<
                    Cons<Cons<T>>,
                    NonNullable<P>, // Unit["objective"]
                    /* eslint-disable @typescript-eslint/ban-types */
                    {}
                  >
                >
              : ParsedGraphQLResult<
                  Cons<Cons<T>>,
                  NonNullable<Foo[T[0]]>, // Unit["course"]
                  /* eslint-disable @typescript-eslint/ban-types */
                  {}
                >;
          }
        >
        : Scalars["AWSJSON"] extends Foo[T[0]] // special handling for AWSJSON because it's a scalar but we want to treat it like an object 
          ? ParsedGraphQLResult<Cons<T>, Foo, SyntesizedType & { [key in T[0]]: Scalars["AWSJSON"] }>
          : Scalars["AWSDateTime"] extends Foo[T[0]] // special handling for AWSDateTime because it's a scalar but we want to treat it like an object
            ? ParsedGraphQLResult<Cons<T>, Foo, SyntesizedType & { [key in T[0]]: Exclude< Foo[T[0]], Date> }>
            : { error: "Encountered non-scalar key with no nested object", key: T[0], value: Foo[T[0]]}
      : T[0] extends "}"
        ? SyntesizedType
        :{ error: "idk yet", key: T[0], nsk: NonScalarKeys<Foo>, sk: ScalarKeys<Foo>, schema: Foo };

export type AFulLGraphQlQuery<
  Inner extends string,
  QueryType extends Exclude<keyof Query, "__typename">,
  InputType extends string,
> = `
  query ${string}(${InputType}) {
    ${QueryType}(${string}) {
      ${Inner}
    }
  }
`;

export type AFullGraphQlMutation<
  Inner extends string,
  MutationType extends Exclude<keyof Mutation, "__typename">,
  InputType extends string,
> = `
  mutation ${string}(${InputType}) {
    ${MutationType}(${string}) {
      ${Inner}
    }
  }
`;

export type ParseGraphQL<Fields extends string, Foo> = ExpandRecursively<
  ParsedGraphQLResult<Split<Fields, " ">, NonNullable<Foo>>
>;

type UnwrapGraphQLQuery<TQuery> =
  TQuery extends GraphQLQuery<infer TData>
    ? TData extends { [key in infer TKey]: any }
      ? TData[TKey]
      : never
    : never;

// prettier-ignore
export type UnwrapGraphQLResult<TMethod extends (...args: any[]) => any> =
  ReturnType<TMethod> extends Promise<infer TResult extends GraphQLResult>
    ? UnwrapGraphQLQuery<TResult["data"]>
    : ReturnType<TMethod> extends Promise<infer T>
      ? T
      : never;

type InferResult<TQuery> =
  TQuery extends AFulLGraphQlQuery<
    infer Fields,
    infer QueryType,
    infer InputType
  >
    ? UnwrapGraphQLResult<
        ReturnType<typeof typedGql<Fields, QueryType, InputType>>
      >
    : never;

/**
 * Used to refer to the actual data return type of a typed-gql query
 *
 * Ex:
 *
 * ```
 * const query = `
 *  query GetThing {
 *    getThing {
 *      id
 *      sk
 *    }
 *  }
 * `
 *
 * const result: UnwrappedTypedGqlResult<typeof query>
 * // { sk: string, id: string }
 * ```
 */
export type UnwrappedTypedGqlResult<TQuery> = InferResult<TQuery>;

/**
 * Used to refer to the promised return type of the typedGql method<br>
 *
 * Ex:
 * ```
 * const query = `
 *  query GetThing {
 *    getThing {
 *      id
 *      sk
 *    }
 *  }
 * `
 *
 * const result: TypedGqlResult<typeof query>
 * // Promise<GraphQLResult<GraphQLQuery<{
 * //   getThing: { sk: string, id: string }
 * // }>>>
 * ```
 */
export type TypedGqlResult<TQuery> =
  TQuery extends AFulLGraphQlQuery<
    infer Fields,
    infer QueryType,
    infer InputType
  >
    ? ReturnType<ReturnType<typeof typedGql<Fields, QueryType, InputType>>>
    : never;

export const typedGql = <
  Fields extends string,
  QueryType extends keyof QueryArgs,
  InputType extends string,
>(
  query: AFulLGraphQlQuery<Fields, QueryType, InputType>,
) => {
  type guessedInputType = NonNullable<QueryArgs[QueryType]>;
  type queryType = GraphQLQuery<{
    [key in QueryType]: ExpandRecursively<
      ParsedGraphQLResult<Split<Fields, " ">, NonNullable<Query[QueryType]>>
    >;
  }>;

  // todo: ensure expected input type (from schema) matches
  // what they said they are providing
  // type CheckedInputType = AssertSame<
  //   InputFromParsedInput<RecursiveInputParser<InputType>>,
  //   guessedInputType
  // >;

  return async (variables: Expand<guessedInputType>) => {
    return GraphQLAPI.client.query<queryType>({
      query,
      variables,
    }) as Promise<GraphQLResult<queryType>>;
  };
};

export const typedGqlMutation = <
  Fields extends string,
  MutationType extends keyof MutationArgs,
  InputType extends string,
>(
  query: AFullGraphQlMutation<Fields, MutationType, InputType>,
) => {
  type guessedInputType = NonNullable<MutationArgs[MutationType]>;
  type mutationType = GraphQLQuery<{
    [key in MutationType]: ExpandRecursively<
      ParsedGraphQLResult<
        Split<Fields, " ">,
        NonNullable<Mutation[MutationType]>
      >
    >;
  }>;

  return async (variables: Expand<guessedInputType>) => {
    return GraphQLAPI.query<mutationType>({
      query,
      variables,
    }) as Promise<GraphQLResult<mutationType>>;
  };
};
